import { formatDate, getTodayFormatted } from '../../../utils/dateUtils';
import floorplanService from '../../../Pages/Siteplans/services/floorplanService';

/**
 * Status colors for PDF rendering, matching the app theme
 */
const STATUS_COLORS = {
    Submitted: '#F59E0B',
    Accepted: '#2E5CFF',
    Assigned: '#F59E0B',
    'Work Completed': '#2E5CFF',
    'Internally Signed Off': '#F59E0B',
    Completed: '#10B981',
    Rejected: '#EF4444',
};

/**
 * PDF layout constants (all values in points, Letter size)
 */
const PAGE_WIDTH = 612;
const PAGE_HEIGHT = 792;
const MARGIN = 54; // 0.75"
const CONTENT_WIDTH = PAGE_WIDTH - 2 * MARGIN;
const HEADER_HEIGHT = 54;
const FOOTER_HEIGHT = 30;
const CONTENT_TOP = MARGIN + HEADER_HEIGHT + 10;
const CONTENT_BOTTOM = PAGE_HEIGHT - MARGIN - FOOTER_HEIGHT;

// Location thumbnail dimensions in the PDF (points)
const THUMB_SIZE = 80;

// Deficiency photo downscale targets — the longest edge of the source bitmap
// is clamped to this many pixels before being re-encoded as JPEG. The photos
// only render at 120x90 pt in the PDF, so 720 px stays sharp well past 400% zoom
// while shrinking embedded bytes ~50-100x vs. the full-resolution originals.
const PHOTO_MAX_EDGE = 720;
const PHOTO_JPEG_QUALITY = 0.72;

// Colors
const PRIMARY = '#0B1537';
const GREY_400 = '#9BA2B0';
const GREY_200 = '#E4E7EE';
const GREY_600 = '#4C5563';
const PAGE_BG = '#FFFFFF'; // White background for the entire page to avoid logo white box rendering issue
const CARD_BG = '#FFFFFF'; // White background for cards
const BORDER_COLOR = '#E5E7EB'; // Subtle border color for cards and images
const TEXT_MUTED = '#6B7280'; // Slightly darker than GREY_400 for better readability

/**
 * Format a currency value for the PDF
 */
const formatCurrency = (value) => {
    if (value === null || value === undefined || value === 0) return null;
    // eslint-disable-next-line no-undef
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
    }).format(value);
};

/**
 * Fetch an image and convert to base64 data URL
 * Returns null if fetch fails (graceful degradation)
 */
const fetchImageAsBase64 = async (url) => {
    try {
        const response = await fetch(url, { credentials: 'include' });
        if (!response.ok) return null;
        const blob = await response.blob();
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(blob);
        });
    } catch {
        return null;
    }
};

/**
 * Fetch a deficiency photo, downscale it to PHOTO_MAX_EDGE on the longest side,
 * and return a JPEG data URL. Aspect ratio is preserved; the on-page render box
 * in the PDF is unchanged — we just hand jsPDF a much smaller bitmap so the
 * resulting file size is dramatically reduced.
 *
 * Returns null if fetch/decode fails (graceful degradation).
 */
const fetchAndDownscalePhoto = async (url) => {
    let objectUrl = null;
    try {
        const response = await fetch(url, { credentials: 'include' });
        if (!response.ok) return null;
        const blob = await response.blob();

        objectUrl = URL.createObjectURL(blob);
        const img = await new Promise((resolve) => {
            const image = new Image();
            image.onload = () => resolve(image);
            image.onerror = () => resolve(null);
            image.src = objectUrl;
        });
        if (!img) return null;

        const srcW = img.naturalWidth || img.width;
        const srcH = img.naturalHeight || img.height;
        if (!srcW || !srcH) return null;

        let targetW = srcW;
        let targetH = srcH;
        const longest = Math.max(srcW, srcH);
        if (longest > PHOTO_MAX_EDGE) {
            const scale = PHOTO_MAX_EDGE / longest;
            targetW = Math.max(1, Math.round(srcW * scale));
            targetH = Math.max(1, Math.round(srcH * scale));
        }

        const canvas = document.createElement('canvas');
        canvas.width = targetW;
        canvas.height = targetH;
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;

        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        // Flatten any alpha onto white so JPEG doesn't render transparent pixels as black
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, targetW, targetH);
        ctx.drawImage(img, 0, 0, targetW, targetH);

        return canvas.toDataURL('image/jpeg', PHOTO_JPEG_QUALITY);
    } catch {
        return null;
    } finally {
        if (objectUrl) URL.revokeObjectURL(objectUrl);
    }
};

/**
 * Load an image URL into an HTMLImageElement (needed for canvas cropping)
 * Returns null on failure
 */
const loadImage = (url) => {
    return new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = 'use-credentials';
        img.onload = () => resolve(img);
        img.onerror = () => resolve(null);
        img.src = url;
    });
};

/**
 * Generate a cropped floorplan thumbnail centered on a pin location
 * Mirrors the logic from LocationSection's FloorplanThumbnail component
 *
 * @param {HTMLImageElement} floorplanImg - The loaded floorplan image
 * @param {number} xPct - Pin X position as decimal (0-1)
 * @param {number} yPct - Pin Y position as decimal (0-1)
 * @returns {string|null} Base64 data URL of the cropped thumbnail
 */
const generateLocationThumbnail = (floorplanImg, xPct, yPct) => {
    try {
        const canvas = document.createElement('canvas');
        const canvasSize = 300; // render at 2x for quality
        canvas.width = canvasSize;
        canvas.height = canvasSize;
        const ctx = canvas.getContext('2d');

        // 25% zoom — show a quarter of the image around the pin
        const zoomFactor = 0.25;
        const cropWidth = floorplanImg.width * zoomFactor;
        const cropHeight = floorplanImg.height * zoomFactor;

        const pinX = xPct * floorplanImg.width;
        const pinY = yPct * floorplanImg.height;

        let cropX = pinX - cropWidth / 2;
        let cropY = pinY - cropHeight / 2;

        // Clamp to image bounds
        cropX = Math.max(0, Math.min(cropX, floorplanImg.width - cropWidth));
        cropY = Math.max(0, Math.min(cropY, floorplanImg.height - cropHeight));

        // Draw cropped region
        ctx.drawImage(
            floorplanImg,
            cropX,
            cropY,
            cropWidth,
            cropHeight,
            0,
            0,
            canvasSize,
            canvasSize
        );

        // Draw pin marker
        const canvasPinX = ((pinX - cropX) / cropWidth) * canvasSize;
        const canvasPinY = ((pinY - cropY) / cropHeight) * canvasSize;

        // Pin shadow
        ctx.beginPath();
        ctx.arc(canvasPinX, canvasPinY + 2, 6, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(0,0,0,0.25)';
        ctx.fill();

        // Pin body (teardrop shape)
        ctx.beginPath();
        ctx.arc(canvasPinX, canvasPinY - 14, 10, Math.PI, 0);
        ctx.quadraticCurveTo(canvasPinX + 10, canvasPinY - 4, canvasPinX, canvasPinY);
        ctx.quadraticCurveTo(canvasPinX - 10, canvasPinY - 4, canvasPinX - 10, canvasPinY - 14);
        ctx.fillStyle = '#EF4444';
        ctx.fill();
        ctx.strokeStyle = '#FFFFFF';
        ctx.lineWidth = 2;
        ctx.stroke();

        // Pin dot
        ctx.beginPath();
        ctx.arc(canvasPinX, canvasPinY - 14, 4, 0, Math.PI * 2);
        ctx.fillStyle = '#FFFFFF';
        ctx.fill();

        return canvas.toDataURL('image/png');
    } catch {
        return null;
    }
};

/**
 * Pre-fetch all images for a list of deficiencies
 * Includes deficiency photos (3 before + 3 after each)
 * and floorplan location thumbnails
 */
const prefetchAllImages = async (deficiencies, getImageUrl, onProgress) => {
    const imageMap = new Map();
    const locationThumbnails = new Map(); // keyed by deficiencyId
    const tasks = [];

    // ——— Deficiency photos ———
    deficiencies.forEach((def) => {
        const beforeImages = (def.beforeImages || []).slice(0, 3);
        const afterImages = (def.afterImages || []).slice(0, 3);
        const allImages = [...beforeImages, ...afterImages];

        allImages.forEach((img) => {
            const key = img.imageUrl || img.s3Key;
            if (key && !imageMap.has(key)) {
                imageMap.set(key, null); // placeholder
                const url = getImageUrl(key);
                if (url) {
                    tasks.push(
                        fetchAndDownscalePhoto(url).then((data) => {
                            imageMap.set(key, data);
                        })
                    );
                }
            }
        });
    });

    // ——— Floorplan location thumbnails ———
    // Collect unique floorplan IDs, then fetch each once and reuse
    const floorplanIds = [
        ...new Set(
            deficiencies
                .filter((d) => d.floorplanId && d.xPct !== undefined && d.yPct !== undefined)
                .map((d) => d.floorplanId)
        ),
    ];

    // Fetch floorplan data + images in parallel (one per unique floorplan)
    const floorplanImageCache = new Map(); // floorplanId → HTMLImageElement
    const floorplanTasks = floorplanIds.map(async (fpId) => {
        try {
            const fpData = await floorplanService.getFloorplanById(fpId);
            if (fpData?.imageUrl) {
                const img = await loadImage(fpData.imageUrl);
                if (img) {
                    floorplanImageCache.set(fpId, img);
                }
            }
        } catch {
            // Skip floorplans that fail to load
        }
    });

    // Run all fetches in parallel
    await Promise.allSettled([...tasks, ...floorplanTasks]);

    // Generate cropped thumbnails for each deficiency that has pin coordinates
    deficiencies.forEach((def) => {
        if (def.floorplanId && def.xPct !== undefined && def.yPct !== undefined) {
            const fpImg = floorplanImageCache.get(def.floorplanId);
            if (fpImg) {
                const thumbnail = generateLocationThumbnail(fpImg, def.xPct, def.yPct);
                if (thumbnail) {
                    locationThumbnails.set(def.deficiencyId, thumbnail);
                }
            }
        }
    });

    if (onProgress) onProgress(0.5); // halfway after image fetch

    return { imageMap, locationThumbnails, floorplanImageCache };
};

/** Max canvas dimension for legend composite (memory / perf guard) */
const LEGEND_MAX_CANVAS_WIDTH = 1600;
const LEGEND_MAX_CANVAS_HEIGHT = 1200;
/** Max height of legend image on PDF page (points) */
const LEGEND_MAX_PDF_IMAGE_HEIGHT = 280;

const computeLegendPdfImageSize = (pixelWidth, pixelHeight) => {
    let drawW = CONTENT_WIDTH;
    let drawH = (pixelHeight / pixelWidth) * drawW;
    if (drawH > LEGEND_MAX_PDF_IMAGE_HEIGHT) {
        drawH = LEGEND_MAX_PDF_IMAGE_HEIGHT;
        drawW = (pixelWidth / pixelHeight) * drawH;
    }
    return { drawW, drawH };
};

/**
 * @param {HTMLImageElement} floorplanImg
 * @param {Array<{ reportIndex: number, xPct: number, yPct: number, status: string }>} markers
 * @returns {{ dataUrl: string, width: number, height: number } | null}
 */
const generateFloorplanLegendComposite = (floorplanImg, markers) => {
    if (!markers.length) return null;
    try {
        const iw = floorplanImg.naturalWidth || floorplanImg.width;
        const ih = floorplanImg.naturalHeight || floorplanImg.height;
        if (!iw || !ih) return null;

        let cw = Math.min(LEGEND_MAX_CANVAS_WIDTH, iw);
        let ch = (ih / iw) * cw;
        if (ch > LEGEND_MAX_CANVAS_HEIGHT) {
            ch = LEGEND_MAX_CANVAS_HEIGHT;
            cw = (iw / ih) * ch;
        }

        const canvas = document.createElement('canvas');
        canvas.width = Math.round(cw);
        canvas.height = Math.round(ch);
        const ctx = canvas.getContext('2d');
        if (!ctx) return null;

        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(floorplanImg, 0, 0, iw, ih, 0, 0, canvas.width, canvas.height);

        const scaleX = canvas.width;
        const scaleY = canvas.height;
        const placed = [];

        markers.forEach((m) => {
            let px = m.xPct * scaleX;
            let py = m.yPct * scaleY;
            let attempts = 0;
            while (attempts < 14) {
                let conflict = false;
                for (let pi = 0; pi < placed.length; pi++) {
                    const p = placed[pi];
                    if (Math.hypot(px - p.x, py - p.y) < 22) {
                        conflict = true;
                        break;
                    }
                }
                if (!conflict) break;
                const angle = attempts * 0.85;
                const r = 10 + attempts * 5;
                px = m.xPct * scaleX + Math.cos(angle) * r;
                py = m.yPct * scaleY + Math.sin(angle) * r;
                px = Math.max(18, Math.min(canvas.width - 18, px));
                py = Math.max(18, Math.min(canvas.height - 18, py));
                attempts += 1;
            }
            placed.push({ x: px, y: py });

            const color = STATUS_COLORS[m.status] || GREY_400;
            const radius = Math.max(12, Math.min(18, Math.round(canvas.width / 70)));

            ctx.beginPath();
            ctx.arc(px, py, radius, 0, Math.PI * 2);
            ctx.fillStyle = color;
            ctx.fill();
            ctx.strokeStyle = '#FFFFFF';
            ctx.lineWidth = 2.5;
            ctx.stroke();

            ctx.fillStyle = '#FFFFFF';
            const fontPx = Math.max(10, Math.round(radius * 1.05));
            ctx.font = `bold ${fontPx}px helvetica,arial,sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(String(m.reportIndex), px, py);
        });

        const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
        return { dataUrl, width: canvas.width, height: canvas.height };
    } catch {
        return null;
    }
};

/**
 * @param {Array} deficiencies - export order defines report indices
 * @returns {Array<{ floorplanId: string, floorplanName: string, groupName: string, markers: Array<{ reportIndex: number, xPct: number, yPct: number, status: string, def: object }> }>}
 */
const buildLegendGroups = (deficiencies) => {
    const map = new Map();
    deficiencies.forEach((def, idx) => {
        if (
            !def.floorplanId ||
            def.xPct === undefined ||
            def.yPct === undefined ||
            def.xPct === null ||
            def.yPct === null
        ) {
            return;
        }
        if (!map.has(def.floorplanId)) {
            map.set(def.floorplanId, {
                floorplanId: def.floorplanId,
                floorplanName: def.floorplanName || 'Floor plan',
                groupName: def.floorplanGroupName || '',
                markers: [],
            });
        }
        map.get(def.floorplanId).markers.push({
            reportIndex: idx + 1,
            xPct: Number(def.xPct),
            yPct: Number(def.yPct),
            status: def.status,
            def,
        });
    });

    const groups = [...map.values()];
    groups.sort((a, b) => {
        const ga = a.groupName || '';
        const gb = b.groupName || '';
        if (ga !== gb) return ga.localeCompare(gb);
        return (a.floorplanName || '').localeCompare(b.floorplanName || '');
    });
    groups.forEach((g) => {
        g.markers.sort((x, y) => x.reportIndex - y.reportIndex);
    });
    return groups;
};

/**
 * Wrap text to fit within a given width, returning an array of lines
 */
const wrapText = (pdf, text, maxWidth) => {
    if (!text) return [];
    const lines = pdf.splitTextToSize(text, maxWidth);
    return lines;
};

/**
 * Get the due date state label and color
 */
const getDueDateInfo = (deficiency) => {
    const { dueDate, dueDateState } = deficiency;
    if (!dueDate) return null;

    const formatted = formatDate(dueDate);
    let color = GREY_600;
    let label = 'On Track';

    if (dueDateState === 'OVERDUE') {
        color = '#EF4444';
        label = 'Overdue';
    } else if (dueDateState === 'DUE_SOON') {
        color = '#F59E0B';
        label = 'Due Soon';
    }

    return { formatted, color, label };
};

/**
 * Build active filters summary text
 */
const buildFilterSummary = (filters) => {
    const parts = [];

    if (filters.statusFilters?.length > 0) {
        parts.push(`Status: ${filters.statusFilters.join(', ')}`);
    }
    if (filters.companyFilters?.length > 0) {
        parts.push(`Company: ${filters.companyFilters.map((c) => c.companyName).join(', ')}`);
    }
    if (filters.locationFilters?.length > 0) {
        const locations = filters.locationFilters.map((l) =>
            l.roomName ? `${l.floorplanName} > ${l.roomName}` : l.floorplanName
        );
        parts.push(`Location: ${locations.join(', ')}`);
    }
    if (filters.internalOnlyFilter) {
        parts.push('Internal Only');
    }
    if (filters.showCompleted) {
        parts.push('Showing Completed');
    }
    if (filters.showRejected) {
        parts.push('Showing Rejected');
    }
    if (filters.searchQuery) {
        parts.push(`Search: "${filters.searchQuery}"`);
    }

    return parts.length > 0 ? parts.join('  |  ') : 'No filters applied';
};

/**
 * Build status breakdown stats
 */
const buildStatusBreakdown = (deficiencies, showDeferredCost = true) => {
    const counts = {};
    let totalDeferredCost = 0;

    deficiencies.forEach((d) => {
        const status = d.status || 'Unknown';
        counts[status] = (counts[status] || 0) + 1;
        if (showDeferredCost && d.deferredCost) totalDeferredCost += d.deferredCost;
    });

    return { counts, totalDeferredCost };
};

/**
 * Generate a professional PDF report from filtered deficiency data
 *
 * @param {Object} options
 * @param {Array} options.deficiencies - Filtered deficiency array
 * @param {Object} options.filters - Active filter state
 * @param {Function} options.getImageUrl - Function to convert image key to URL
 * @param {Object} [options.projectInfo] - Project info (name, address, businessName)
 * @param {boolean} [options.showDeferredCost] - Whether deferred-cost fields should be shown
 * @param {Function} [options.onProgress] - Progress callback (0-1)
 */
const generateDeficiencyPdf = async ({
    deficiencies,
    filters,
    getImageUrl,
    projectInfo,
    showDeferredCost = true,
    onProgress,
}) => {
    const { jsPDF } = await import('jspdf');

    const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: 'letter',
    });

    let currentY = CONTENT_TOP;
    let pageCount = 1;

    // Pre-fetch brand logo (pre-trimmed PNG, 1155x400, aspect ~2.89:1)
    let logoData = null;
    try {
        logoData = await fetchImageAsBase64(`${window.location.origin}/moonlight-connect-logo.png`);
    } catch {
        // Logo is optional — continue without it
    }

    // Pre-fetch all images (deficiency photos + floorplan location thumbnails)
    const { imageMap, locationThumbnails, floorplanImageCache } = await prefetchAllImages(
        deficiencies,
        getImageUrl,
        onProgress
    );

    // Logo dimensions — trimmed image is 2.89:1 aspect ratio
    const LOGO_ASPECT = 2.89;

    // ——— Helper functions ———

    const COMPACT_HEADER_HEIGHT = 26;

    const drawHeader = (isFirstPage = false) => {
        // Logo (left) .................. Date (right)
        const logoH = 16;
        const logoW = logoH * LOGO_ASPECT;

        if (logoData) {
            try {
                pdf.addImage(logoData, 'PNG', MARGIN, MARGIN, logoW, logoH);
            } catch {
                // ignore
            }
        }

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor(GREY_400);
        pdf.text(`Generated ${formatDate(new Date())}`, PAGE_WIDTH - MARGIN, MARGIN + 11, {
            align: 'right',
        });

        if (isFirstPage) {
            // "Deficiency Report" centered on second line — first page only
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(14);
            pdf.setTextColor(PRIMARY);
            pdf.text('Deficiency Report', PAGE_WIDTH / 2, MARGIN + 36, { align: 'center' });

            // Project info section below title (left-aligned)
            let projectInfoY = MARGIN + 52;
            if (projectInfo) {
                pdf.setFont('helvetica', 'normal');
                pdf.setFontSize(9);
                pdf.setTextColor(TEXT_MUTED);

                // Line 1: Project name
                if (projectInfo.name) {
                    pdf.text(projectInfo.name, MARGIN, projectInfoY);
                    projectInfoY += 14;
                }

                // Line 2: Address
                if (projectInfo.address) {
                    pdf.text(projectInfo.address, MARGIN, projectInfoY);
                    projectInfoY += 14;
                }

                // Line 2: General contractor
                if (projectInfo.businessName) {
                    pdf.setFontSize(8.5);
                    pdf.text(
                        `General Contractor: ${projectInfo.businessName}`,
                        MARGIN,
                        projectInfoY
                    );
                    projectInfoY += 14;
                }
            }

            pdf.setDrawColor(GREY_200);
            pdf.setLineWidth(0.5);
            pdf.line(MARGIN, projectInfoY, PAGE_WIDTH - MARGIN, projectInfoY);
        } else {
            // Compact separator on subsequent pages
            pdf.setDrawColor(GREY_200);
            pdf.setLineWidth(0.5);
            pdf.line(
                MARGIN,
                MARGIN + COMPACT_HEADER_HEIGHT,
                PAGE_WIDTH - MARGIN,
                MARGIN + COMPACT_HEADER_HEIGHT
            );
        }
    };

    const drawFooter = (pageNum) => {
        const footerY = PAGE_HEIGHT - MARGIN;
        pdf.setDrawColor(GREY_200);
        pdf.setLineWidth(0.5);
        pdf.line(MARGIN, footerY - 15, PAGE_WIDTH - MARGIN, footerY - 15);

        // Brand logo in footer (small, left-aligned)
        if (logoData) {
            const fLogoH = 14;
            const fLogoW = fLogoH * LOGO_ASPECT;
            try {
                pdf.addImage(logoData, 'PNG', MARGIN, footerY - 10, fLogoW, fLogoH);
            } catch {
                pdf.setFont('helvetica', 'normal');
                pdf.setFontSize(8);
                pdf.setTextColor(GREY_400);
                pdf.text('Moonlight Connect', MARGIN, footerY);
            }
        } else {
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(8);
            pdf.setTextColor(GREY_400);
            pdf.text('Moonlight Connect', MARGIN, footerY);
        }

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor(GREY_400);
        // Placeholder — will be overwritten with total page count at the end
        pdf.text(`Page ${pageNum} of __TOTAL__`, PAGE_WIDTH - MARGIN, footerY, { align: 'right' });
    };

    const newPage = () => {
        pdf.addPage();
        pageCount++;

        // Fill entire new page with background color
        pdf.setFillColor(PAGE_BG);
        pdf.rect(0, 0, PAGE_WIDTH, PAGE_HEIGHT, 'F');

        drawHeader(false);
        drawFooter(pageCount);
        currentY = MARGIN + COMPACT_HEADER_HEIGHT + 15;
    };

    const checkPageBreak = (neededHeight) => {
        if (currentY + neededHeight > CONTENT_BOTTOM) {
            newPage();
            return true;
        }
        return false;
    };

    const drawStatusBadge = (status, x, y) => {
        const color = STATUS_COLORS[status] || GREY_400;
        const label = status || 'Unknown';

        pdf.setFontSize(7);
        const textWidth = pdf.getTextWidth(label);
        const badgeWidth = textWidth + 10;
        const badgeHeight = 14;

        // Rounded rect background
        pdf.setFillColor(color);
        pdf.roundedRect(x, y - 10, badgeWidth, badgeHeight, 3, 3, 'F');

        // White text
        pdf.setTextColor('#FFFFFF');
        pdf.setFont('helvetica', 'bold');
        pdf.text(label, x + 5, y);

        return badgeWidth + 6;
    };

    // ——— Draw first page background, header + footer ———
    // Fill entire first page with background color
    pdf.setFillColor(PAGE_BG);
    pdf.rect(0, 0, PAGE_WIDTH, PAGE_HEIGHT, 'F');

    drawHeader(true);
    drawFooter(1);

    // Adjust starting Y for project info extra height on first page
    if (projectInfo) {
        let extraHeight = 0;
        if (projectInfo.name) extraHeight += 14;
        if (projectInfo.address) extraHeight += 14;
        if (projectInfo.businessName) extraHeight += 14;
        if (extraHeight > 0) currentY = CONTENT_TOP + extraHeight;
    }

    // ——— Summary Section Card ———
    const filterSummary = buildFilterSummary(filters);
    const { counts, totalDeferredCost } = buildStatusBreakdown(deficiencies, showDeferredCost);

    const filterLines = wrapText(pdf, filterSummary, CONTENT_WIDTH - MARGIN);

    // Prepare status lines for height calculation
    const orderedStatuses = [
        'Submitted',
        'Accepted',
        'Assigned',
        'Work Completed',
        'Internally Signed Off',
        'Completed',
        'Rejected',
    ];

    const definedCounts = orderedStatuses
        .filter((status) => counts[status] > 0)
        .map((status) => `${status}: ${counts[status]}`);

    // Add any unexpected statuses not in the ordered list
    Object.keys(counts).forEach((status) => {
        if (!orderedStatuses.includes(status) && counts[status] > 0) {
            definedCounts.push(`${status}: ${counts[status]}`);
        }
    });

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8.5);

    // Calculate max width for status lines based on card padding and initial offset (120)
    const cardPadding = 15;
    const maxStatusWidth = CONTENT_WIDTH - cardPadding * 2 - 120;

    // Manually build lines to prevent splitting status items (e.g. 'Internally Signed Off: 1')
    const statusLines = [];
    let currentLine = '';

    definedCounts.forEach((item) => {
        const testLine = currentLine ? currentLine + '   |   ' + item : item;
        if (pdf.getTextWidth(testLine) > maxStatusWidth && currentLine !== '') {
            statusLines.push(currentLine);
            currentLine = item;
        } else {
            currentLine = testLine;
        }
    });
    if (currentLine) {
        statusLines.push(currentLine);
    }

    // Calculate Summary Card Height
    let summaryCardInnerHeight = 20; // Title (20 space below)
    summaryCardInnerHeight += filterLines.length * 12 + 8; // filters + 8 space

    // Stats taking space. We calculate height based on the number of status lines
    const statsHeight = Math.max(16, statusLines.length * 12 + 4);
    summaryCardInnerHeight += statsHeight;

    if (totalDeferredCost > 0) summaryCardInnerHeight += 16;

    let summaryCardHeight = summaryCardInnerHeight + cardPadding * 2;

    // Draw Summary Card Background
    pdf.setFillColor(CARD_BG);
    pdf.setDrawColor(BORDER_COLOR);
    pdf.setLineWidth(0.5);
    pdf.roundedRect(MARGIN, currentY, CONTENT_WIDTH, summaryCardHeight, 6, 6, 'FD');

    // Internal margin for the card
    let cardCurrentY = currentY + cardPadding + 10; // +10 for font baseline adjustment

    // Title
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(12);
    pdf.setTextColor(PRIMARY);
    pdf.text('Report Summary', MARGIN + cardPadding, cardCurrentY);
    cardCurrentY += 20;

    // Filter summary
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8.5);
    pdf.setTextColor(TEXT_MUTED);
    filterLines.forEach((line) => {
        pdf.text(line, MARGIN + cardPadding, cardCurrentY);
        cardCurrentY += 12;
    });
    cardCurrentY += 8;

    // Stats row line
    pdf.setDrawColor(GREY_200);
    pdf.setLineWidth(0.5);
    pdf.line(
        MARGIN + cardPadding,
        cardCurrentY - 6,
        PAGE_WIDTH - MARGIN - cardPadding,
        cardCurrentY - 6
    );

    // Stats
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(9);
    pdf.setTextColor(PRIMARY);
    pdf.text(`Total Deficiencies: ${deficiencies.length}`, MARGIN + cardPadding, cardCurrentY + 6);

    // Status breakdown inline
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8.5);
    pdf.setTextColor(TEXT_MUTED);
    statusLines.forEach((line, idx) => {
        pdf.text(line, MARGIN + cardPadding + 120, cardCurrentY + 6 + idx * 12);
    });
    cardCurrentY += statsHeight;

    if (totalDeferredCost > 0) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(9);
        pdf.setTextColor(PRIMARY);
        pdf.text(`Total Deferred Cost: `, MARGIN + cardPadding, cardCurrentY + 6);

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(TEXT_MUTED);
        pdf.text(formatCurrency(totalDeferredCost), MARGIN + cardPadding + 100, cardCurrentY + 6);
    }

    // Move Y down past the summary card
    currentY += summaryCardHeight + 20;

    // ——— Floor plan legends (full sheet; # = deficiency order below) ———
    const legendGroups = buildLegendGroups(deficiencies);
    if (legendGroups.length > 0) {
        const sectionPad = 14;
        const listMaxW = CONTENT_WIDTH - sectionPad * 2 - 8;

        pdf.setFont('helvetica', 'italic');
        pdf.setFontSize(8);
        pdf.setTextColor(TEXT_MUTED);
        const introLines = wrapText(
            pdf,
            'Numbers on floor plans match deficiency order in this report (# on map = Nth deficiency below).',
            CONTENT_WIDTH - 10
        );
        const introH = introLines.length * 11 + 12;
        checkPageBreak(introH + 10);
        introLines.forEach((line) => {
            pdf.text(line, MARGIN + 5, currentY);
            currentY += 11;
        });
        currentY += 10;
        pdf.setFont('helvetica', 'normal');

        for (const group of legendGroups) {
            const fpImg = floorplanImageCache.get(group.floorplanId);
            if (!fpImg) continue;

            const composite = generateFloorplanLegendComposite(
                fpImg,
                group.markers.map(({ reportIndex, xPct, yPct, status }) => ({
                    reportIndex,
                    xPct,
                    yPct,
                    status,
                }))
            );
            if (!composite?.dataUrl) continue;

            const { drawW, drawH } = computeLegendPdfImageSize(composite.width, composite.height);
            const nameLines = wrapText(pdf, group.floorplanName, listMaxW);

            let listH = 10;
            group.markers.forEach((m) => {
                const line = `#${m.reportIndex} — ${m.def.title || 'Untitled'} (${m.def.status || 'Unknown'})`;
                listH += wrapText(pdf, line, listMaxW).length * 11 + 4;
            });

            const titleBlockH = 20 + (group.groupName ? 12 : 0) + nameLines.length * 12 + 8;
            const sectionInner = titleBlockH + drawH + 14 + listH + 12;
            const sectionH = sectionInner + sectionPad * 2;

            checkPageBreak(sectionH + 15);

            const sectionY = currentY;
            pdf.setFillColor(CARD_BG);
            pdf.setDrawColor(BORDER_COLOR);
            pdf.setLineWidth(0.5);
            pdf.roundedRect(MARGIN, sectionY, CONTENT_WIDTH, sectionH, 6, 6, 'FD');

            let cy = sectionY + sectionPad + 12;
            const innerX = MARGIN + sectionPad;

            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(11);
            pdf.setTextColor(PRIMARY);
            pdf.text('Drawing legend', innerX, cy);
            cy += 16;

            if (group.groupName) {
                pdf.setFont('helvetica', 'normal');
                pdf.setFontSize(8.5);
                pdf.setTextColor(TEXT_MUTED);
                pdf.text(`Building: ${group.groupName}`, innerX, cy);
                cy += 12;
            }
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(9);
            pdf.setTextColor(PRIMARY);
            nameLines.forEach((tl) => {
                pdf.text(tl, innerX, cy);
                cy += 12;
            });
            cy += 8;

            const imgXCentered = MARGIN + (CONTENT_WIDTH - drawW) / 2;
            try {
                pdf.addImage(composite.dataUrl, 'JPEG', imgXCentered, cy, drawW, drawH);
            } catch {
                pdf.setFont('helvetica', 'normal');
                pdf.setFontSize(9);
                pdf.setTextColor(TEXT_MUTED);
                pdf.text('Unable to render floor plan image.', innerX, cy + 16);
            }
            cy += drawH + 12;

            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(8.5);
            pdf.setTextColor(PRIMARY);
            pdf.text('Key', innerX, cy);
            cy += 12;

            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(8);
            pdf.setTextColor(GREY_600);
            group.markers.forEach((m) => {
                const line = `#${m.reportIndex} — ${m.def.title || 'Untitled'} (${m.def.status || 'Unknown'})`;
                const lines = wrapText(pdf, line, listMaxW);
                lines.forEach((ln) => {
                    pdf.text(ln, innerX, cy);
                    cy += 11;
                });
                cy += 4;
            });

            currentY = sectionY + sectionH + 12;
        }
    }

    // ——— Deficiency Cards ———
    for (let i = 0; i < deficiencies.length; i++) {
        const def = deficiencies[i];

        if (onProgress) {
            onProgress(0.5 + (i / deficiencies.length) * 0.5);
        }

        // Estimate card content height
        const cardPadding = 12;
        const innerWidth = CONTENT_WIDTH - cardPadding * 2;

        const descLines = wrapText(pdf, def.description, innerWidth);
        const hasBeforeImages =
            def.beforeImages &&
            def.beforeImages.length > 0 &&
            def.beforeImages.some((img) => imageMap.get(img.imageUrl || img.s3Key));
        const hasAfterImages =
            def.afterImages &&
            def.afterImages.length > 0 &&
            def.afterImages.some((img) => imageMap.get(img.imageUrl || img.s3Key));
        const hasLocationThumb = locationThumbnails.has(def.deficiencyId);

        // Image rows need more padding inside the card
        const imgWidth = 120; // smaller to comfortably fit 3 and reduce height
        const imgHeight = 90;
        const imageRowHeight = imgHeight + 25; // height + label + spacing

        // Calculate wrapped row heights for dynamic card sizing
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(8);
        const estimatedColWidth =
            (hasLocationThumb ? innerWidth - THUMB_SIZE - 15 : innerWidth) / 2;
        const valMaxWidth = estimatedColWidth - 70;

        const roomLines = wrapText(pdf, def.roiName || 'N/A', valMaxWidth);
        const roomExtraHeight = Math.max(0, (roomLines.length - 1) * 12);

        const drawingLines = wrapText(pdf, def.floorplanName || 'N/A', valMaxWidth);
        const drawingExtraHeight = Math.max(0, (drawingLines.length - 1) * 12);

        const hasBuildingRow = !!def.floorplanGroupName;
        const baseRowCount = hasBuildingRow ? 5 : 4;
        const detailsHeight = Math.max(
            baseRowCount * 18 + 6 + roomExtraHeight + drawingExtraHeight,
            hasLocationThumb ? THUMB_SIZE + 10 : 0
        );

        // Calculate title height (# matches Drawing legend and key list)
        const badgeWidth = 70; // rough estimate
        const titleMaxWidth = innerWidth - badgeWidth - 10;
        const titleHeadline = `#${i + 1} — ${def.title || 'Untitled'}`;
        const titleLines = wrapText(pdf, titleHeadline, titleMaxWidth);
        const titleHeight = Math.max(titleLines.length * 14, 16);

        let cardInnerHeight =
            titleHeight +
            15 + // header (status + title) + spacing
            detailsHeight +
            10 + // details grid
            (def.description ? descLines.length * 12 + 25 : 0) + // description + label + spacing
            (hasBeforeImages ? imageRowHeight : 0) +
            (hasAfterImages ? imageRowHeight : 0);

        const cardTotalHeight = cardInnerHeight + cardPadding * 2;

        // Check page break BEFORE drawing the card
        checkPageBreak(cardTotalHeight + 15); // +15 for bottom margin

        // ——— Draw Card Enclosure ———
        pdf.setFillColor(CARD_BG);
        pdf.setDrawColor(BORDER_COLOR);
        pdf.setLineWidth(0.5);
        pdf.roundedRect(MARGIN, currentY, CONTENT_WIDTH, cardTotalHeight, 6, 6, 'FD');

        let cardY = currentY + cardPadding;
        const cardX = MARGIN + cardPadding;

        // ——— Card Header: Status Badge + Title ———
        const actualBadgeWidth = drawStatusBadge(def.status, cardX, cardY + 10);

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(11);
        pdf.setTextColor(PRIMARY);

        titleLines.forEach((line, idx) => {
            pdf.text(line, cardX + actualBadgeWidth, cardY + 10 + idx * 14);
        });
        cardY += titleHeight + 15;

        // Subtle separator below header
        pdf.setDrawColor(GREY_200);
        pdf.setLineWidth(0.5);
        pdf.line(cardX, cardY - 5, cardX + innerWidth, cardY - 5);
        cardY += 5;

        // ——— Details Grid + Location Thumbnail ———
        const thumbGap = 15;
        const detailsWidth = hasLocationThumb ? innerWidth - THUMB_SIZE - thumbGap : innerWidth;
        const colWidth = detailsWidth / 2;
        const detailStartY = cardY;

        pdf.setFontSize(8);

        // Track cumulative Y offset for rows (to handle variable-height Room row)
        let detailRowY = detailStartY + 12; // +12 for font baseline

        const drawDetailRow = (label, value, col, valueColor = null) => {
            const x = cardX + col * colWidth;

            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(8);
            pdf.setTextColor(TEXT_MUTED);
            pdf.text(label, x, detailRowY);

            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(8);
            pdf.setTextColor(valueColor || PRIMARY);
            // Truncate if needed
            const maxValWidth = colWidth - 70; // Keep space for the label
            let displayVal = value || 'N/A';
            while (pdf.getTextWidth(displayVal) > maxValWidth && displayVal.length > 3) {
                displayVal = displayVal.slice(0, -4) + '...';
            }
            pdf.text(displayVal, x + 65, detailRowY);
        };

        // Helper to draw a detail row with wrapping value (no truncation)
        const drawDetailRowWrapped = (label, value, col) => {
            const x = cardX + col * colWidth;
            const maxValWidth = colWidth - 70;

            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(8);
            pdf.setTextColor(TEXT_MUTED);
            pdf.text(label, x, detailRowY);

            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(8);
            pdf.setTextColor(PRIMARY);
            const lines = wrapText(pdf, value || 'N/A', maxValWidth);
            lines.forEach((line, idx) => {
                pdf.text(line, x + 65, detailRowY + idx * 12);
            });

            return lines.length; // return number of lines for height tracking
        };

        // Row 0 (conditional): Building + Assigned To (multi-building projects)
        if (def.floorplanGroupName) {
            drawDetailRow('Building', def.floorplanGroupName, 0);
            drawDetailRow('Assigned To', def.assignedToBusinessName || 'Unassigned', 1);
            detailRowY += 18;

            // Row 1: Drawing (wrapped) + Due Date
            const drawingLineCount = drawDetailRowWrapped('Drawing', def.floorplanName || 'N/A', 0);
            const dueDateInfo = getDueDateInfo(def);
            const dueDateVal = dueDateInfo
                ? `${dueDateInfo.formatted} (${dueDateInfo.label})`
                : 'Not set';
            const dueDateColor =
                dueDateInfo && dueDateInfo.label === 'Overdue' ? dueDateInfo.color : null;
            drawDetailRow('Due Date', dueDateVal, 1, dueDateColor);
            detailRowY += Math.max(18, drawingLineCount * 12 + 6);

            // Row 2: Room (wrapped) + Deferred Cost/Created
            const roomLineCount = drawDetailRowWrapped('Room', def.roiName || 'N/A', 0);
            if (showDeferredCost) {
                const costVal = formatCurrency(def.deferredCost);
                drawDetailRow('Deferred Cost', costVal || 'None', 1);
            } else {
                drawDetailRow('Created', formatDate(def.dateCreated || def.createdAt), 1);
            }
            detailRowY += Math.max(18, roomLineCount * 12 + 6);

            if (showDeferredCost) {
                // Row 3: Created
                drawDetailRow('Created', formatDate(def.dateCreated || def.createdAt), 0);
                detailRowY += 18;
            }
        } else {
            // Row 0: Drawing (wrapped) + Assigned To (single-building projects)
            const drawingLineCount = drawDetailRowWrapped('Drawing', def.floorplanName || 'N/A', 0);
            drawDetailRow('Assigned To', def.assignedToBusinessName || 'Unassigned', 1);
            detailRowY += Math.max(18, drawingLineCount * 12 + 6);

            // Row 1: Room (wrapped, no truncation) + Due Date
            const roomLineCount = drawDetailRowWrapped('Room', def.roiName || 'N/A', 0);
            const dueDateInfo = getDueDateInfo(def);
            const dueDateVal = dueDateInfo
                ? `${dueDateInfo.formatted} (${dueDateInfo.label})`
                : 'Not set';
            const dueDateColor =
                dueDateInfo && dueDateInfo.label === 'Overdue' ? dueDateInfo.color : null;
            drawDetailRow('Due Date', dueDateVal, 1, dueDateColor);
            detailRowY += Math.max(18, roomLineCount * 12 + 6);

            // Row 2: Deferred Cost + Created
            if (showDeferredCost) {
                const costVal = formatCurrency(def.deferredCost);
                drawDetailRow('Deferred Cost', costVal || 'None', 0);
            }
            drawDetailRow('Created', formatDate(def.dateCreated || def.createdAt), 1);
            detailRowY += 18;
        }

        // ——— Location Thumbnail (right side of details) ———
        if (hasLocationThumb) {
            const thumbX = cardX + innerWidth - THUMB_SIZE;
            const thumbY = detailStartY + 5;

            // Label above thumbnail
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(8);
            pdf.setTextColor(TEXT_MUTED);
            pdf.text('Map Pin', thumbX, thumbY - 2);

            try {
                pdf.addImage(
                    locationThumbnails.get(def.deficiencyId),
                    'PNG',
                    thumbX,
                    thumbY + 2,
                    THUMB_SIZE,
                    THUMB_SIZE
                );
                // Border around thumbnail
                pdf.setDrawColor(BORDER_COLOR);
                pdf.setLineWidth(1);
                // Subtle shadow effect approximation via double border
                pdf.setDrawColor('#F3F4F6');
                pdf.roundedRect(thumbX - 1, thumbY + 1, THUMB_SIZE + 2, THUMB_SIZE + 2, 4, 4, 'S');
                pdf.setDrawColor(BORDER_COLOR);
                pdf.roundedRect(thumbX, thumbY + 2, THUMB_SIZE, THUMB_SIZE, 4, 4, 'S');
            } catch {
                // Skip if thumbnail fails to render
            }
        }

        cardY = detailStartY + detailsHeight + 15;

        // ——— Description ———
        if (def.description) {
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(8.5);
            pdf.setTextColor(PRIMARY);
            pdf.text('Description', cardX, cardY);
            cardY += 14;

            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(8.5);
            pdf.setTextColor(GREY_600);
            descLines.forEach((line) => {
                pdf.text(line, cardX, cardY);
                cardY += 12;
            });
            cardY += 10;
        }

        // ——— Photos ———
        const drawImageRow = (images, sectionLabel) => {
            const validImages = images
                .slice(0, 3)
                .map((img) => ({
                    data: imageMap.get(img.imageUrl || img.s3Key),
                    key: img.imageUrl || img.s3Key,
                }))
                .filter((img) => img.data);

            if (validImages.length === 0) return;

            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(8.5);
            pdf.setTextColor(PRIMARY);
            pdf.text(sectionLabel, cardX, cardY);
            cardY += 12;

            const imgGap = 10;

            validImages.forEach((img, idx) => {
                const x = cardX + idx * (imgWidth + imgGap);
                try {
                    // Image subtle border
                    pdf.setDrawColor(BORDER_COLOR);
                    pdf.setLineWidth(1);
                    pdf.roundedRect(x - 1, cardY - 1, imgWidth + 2, imgHeight + 2, 3, 3, 'S');

                    pdf.addImage(img.data, '', x, cardY, imgWidth, imgHeight);
                } catch {
                    // Skip images that fail to render
                    pdf.setDrawColor(GREY_200);
                    pdf.setFillColor('#F9FAFB');
                    pdf.rect(x, cardY, imgWidth, imgHeight, 'FD');
                    pdf.setFont('helvetica', 'normal');
                    pdf.setFontSize(8);
                    pdf.setTextColor(TEXT_MUTED);
                    pdf.text('Image unavailable', x + imgWidth / 2, cardY + imgHeight / 2, {
                        align: 'center',
                    });
                }
            });

            cardY += imgHeight + 15;
        };

        if (hasBeforeImages) {
            drawImageRow(def.beforeImages, 'Before Photos');
        }
        if (hasAfterImages) {
            drawImageRow(def.afterImages, 'After Photos');
        }

        // Move the global currentY down to where the NEXT card should start
        currentY = currentY + cardTotalHeight + 15;
    }

    // ——— Stamp total page count on all footers ———
    for (let p = 1; p <= pageCount; p++) {
        pdf.setPage(p);
        // Overwrite the footer text with correct total
        const footerY = PAGE_HEIGHT - MARGIN;

        // White rect to clear old footer text
        pdf.setFillColor(PAGE_BG);
        pdf.rect(PAGE_WIDTH - MARGIN - 80, footerY - 10, 80, 14, 'F');

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor(GREY_400);
        pdf.text(`Page ${p} of ${pageCount}`, PAGE_WIDTH - MARGIN, footerY, { align: 'right' });
    }

    // Save
    const filename = `Deficiency_Report_${getTodayFormatted()}.pdf`;
    pdf.save(filename);

    if (onProgress) onProgress(1);
};

export default generateDeficiencyPdf;
